/**
 * 全局存储模块
 * 用于在不同组件间共享数据，特别是URL参数等需要跨页面传递的信息
 * 采用本地缓存模式，确保返回页面不会丢失参数值
 */

// 检测是否支持localStorage
function isLocalStorageSupported() {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

// 检测是否支持sessionStorage
function isSessionStorageSupported() {
  try {
    const testKey = '__session_test__';
    sessionStorage.setItem(testKey, testKey);
    sessionStorage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
}

// 全局存储对象
const globalStore = {
  // 存储URL参数
  urlParams: {},
  
  // 缓存支持标志
  hasLocalStorage: isLocalStorageSupported(),
  hasSessionStorage: isSessionStorageSupported(),
  
  // 缓存键名
  STORAGE_KEY: 'globalStore_urlParams',
  
  /**
   * 保存数据到本地存储
   * 优先使用localStorage，其次使用sessionStorage
   */
  saveToStorage(data) {
    const jsonData = JSON.stringify(data);
    
    // 优先尝试localStorage
    if (this.hasLocalStorage) {
      try {
        localStorage.setItem(this.STORAGE_KEY, jsonData);
        return true;
      } catch (error) {
        console.warn('localStorage写入失败，尝试使用sessionStorage:', error);
      }
    }
    
    // 如果localStorage不可用或写入失败，尝试sessionStorage
    if (this.hasSessionStorage) {
      try {
        sessionStorage.setItem(this.STORAGE_KEY, jsonData);
        return true;
      } catch (error) {
        console.warn('sessionStorage写入失败:', error);
      }
    }
    
    return false;
  },
  
  /**
   * 从本地存储读取数据
   * 优先尝试localStorage，其次尝试sessionStorage
   */
  loadFromStorage() {
    let jsonData = null;
    
    // 优先尝试localStorage
    if (this.hasLocalStorage) {
      try {
        jsonData = localStorage.getItem(this.STORAGE_KEY);
      } catch (error) {
        console.warn('localStorage读取失败，尝试使用sessionStorage:', error);
      }
    }
    
    // 如果localStorage不可用或读取失败，尝试sessionStorage
    if (!jsonData && this.hasSessionStorage) {
      try {
        jsonData = sessionStorage.getItem(this.STORAGE_KEY);
      } catch (error) {
        console.warn('sessionStorage读取失败:', error);
      }
    }
    
    if (jsonData) {
      try {
        return JSON.parse(jsonData);
      } catch (error) {
        console.warn('解析缓存数据失败:', error);
        // 如果解析失败，清理损坏的数据
        this.clearStorage();
      }
    }
    
    return null;
  },
  
  /**
   * 清理本地存储
   */
  clearStorage() {
    if (this.hasLocalStorage) {
      try {
        localStorage.removeItem(this.STORAGE_KEY);
      } catch (error) {
        console.warn('清理localStorage失败:', error);
      }
    }
    
    if (this.hasSessionStorage) {
      try {
        sessionStorage.removeItem(this.STORAGE_KEY);
      } catch (error) {
        console.warn('清理sessionStorage失败:', error);
      }
    }
  },
  
  /**
   * 设置URL参数
   * @param {Object} params - URL参数对象
   */
  setUrlParams(params) {
    // 合并新参数，而不是完全替换
    this.urlParams = { ...this.urlParams, ...params };
    
    // 保存到本地存储
    this.saveToStorage(this.urlParams);
  },
  
  /**
   * 获取URL参数
   * @returns {Object} URL参数对象
   */
  getUrlParams() {
    // 在获取时再次尝试从存储同步，确保数据最新
    const storedData = this.loadFromStorage();
    if (storedData) {
      this.urlParams = { ...this.urlParams, ...storedData };
    }
    return { ...this.urlParams };
  },
  
  /**
   * 清除URL参数
   * @param {string} key - 可选，指定要清除的键名
   */
  clearUrlParams(key) {
    if (key) {
      // 清除指定键
      if (this.urlParams.hasOwnProperty(key)) {
        delete this.urlParams[key];
      }
    } else {
      // 清除所有参数
      this.urlParams = {};
    }
    
    // 更新本地存储
    this.saveToStorage(this.urlParams);
  },
  
  /**
   * 初始化全局存储
   * 从本地存储恢复参数（如果有）
   */
  init() {
    const storedData = this.loadFromStorage();
    if (storedData) {
      this.urlParams = storedData;
      console.log('全局存储初始化成功，从本地缓存恢复数据');
    } else {
      console.log('全局存储初始化，无缓存数据');
    }
    
    // 添加页面可见性变化监听器，确保页面重新可见时同步数据
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        // 页面重新可见时，从存储同步数据
        const storedData = this.loadFromStorage();
        if (storedData) {
          this.urlParams = { ...this.urlParams, ...storedData };
        }
      }
    });
    
    // 添加页面卸载前保存数据
    window.addEventListener('beforeunload', () => {
      this.saveToStorage(this.urlParams);
    });
  }
};

// 初始化全局存储
globalStore.init();

export default globalStore;